import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Button, Paper, Stack, Alert, Tabs, Tab, Chip,
  TextField, MenuItem, Grid, CircularProgress, Divider, DialogActions,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CloseIcon from '@mui/icons-material/Close';
import {
  getCustomer, updateCustomer,
  deleteContact,
  createInteraction, updateInteraction, deleteInteraction,
  getIndustries, aiSuggestNextSteps,
  getDefaultProfilingTemplate,
} from '../services/api';
import Modal from '../components/Modal';
import { useDialog } from '../components/DialogProvider';
import AzotteGrid from '../components/AzotteGrid';
import countries from '../data/countries';

const INTERACTION_TYPES = ['Email', 'PhoneCall', 'Meeting', 'VideoCall', 'Other'];
const TYPE_COLORS = { Email: 'info', PhoneCall: 'success', Meeting: 'warning', VideoCall: 'secondary', Other: 'default' };

const getScoreColor = (score) => {
  if (score >= 76) return '#22c55e';
  if (score >= 51) return '#3b82f6';
  if (score >= 26) return '#f59e0b';
  return '#ef4444';
};

const getScoreLabel = (score) => {
  if (score >= 76) return 'Great Fit';
  if (score >= 51) return 'Good Fit';
  if (score >= 26) return 'Moderate';
  return 'Low Fit';
};

const OUTCOME_COLORS = { Positive: '#38a169', Neutral: '#dd6b20', Negative: '#e53e3e', NoResponse: '#718096', FollowUpNeeded: '#3182ce' };

const CUSTOMER_TAB_KEYS = ['info', 'profile', 'contacts', 'interactions'];

export default function CustomerDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dialog = useDialog();
  const [searchParams, setSearchParams] = useSearchParams();
  const [customer, setCustomer] = useState(null);
  const initialTab = (() => {
    const t = searchParams.get('tab');
    const idx = CUSTOMER_TAB_KEYS.indexOf(t);
    return idx >= 0 ? idx : 0;
  })();
  const [activeTab, setActiveTab] = useState(initialTab);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [editingInfo, setEditingInfo] = useState(false);
  const [infoForm, setInfoForm] = useState({});
  const [interactionModal, setInteractionModal] = useState(null);
  const [interactionForm, setInteractionForm] = useState({});
  const [selectedInteraction, setSelectedInteraction] = useState(null);
  const [industries, setIndustries] = useState([]);
  const [editingProfile, setEditingProfile] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [suggestingId, setSuggestingId] = useState(null);
  const [profilingTemplate, setProfilingTemplate] = useState(null);
  const [profileForm, setProfileForm] = useState({});

  const [contactsPage, setContactsPage] = useState(0);
  const [contactsRowsPerPage, setContactsRowsPerPage] = useState(10);
  const [interactionsPage, setInteractionsPage] = useState(0);
  const [interactionsRowsPerPage, setInteractionsRowsPerPage] = useState(10);

  const fetchCustomer = useCallback(async () => {
    try {
      const { data } = await getCustomer(id);
      setCustomer(data);
      setInfoForm(data);
    } catch {
      setError('Failed to load customer');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchIndustries = useCallback(async () => {
    try {
      const { data } = await getIndustries();
      setIndustries(data);
    } catch { /* optional */ }
  }, []);

  const fetchProfilingTemplate = useCallback(async () => {
    try {
      const { data } = await getDefaultProfilingTemplate();
      setProfilingTemplate(data);
    } catch { /* optional */ }
  }, []);

  useEffect(() => { fetchCustomer(); fetchIndustries(); fetchProfilingTemplate(); }, [fetchCustomer, fetchIndustries, fetchProfilingTemplate]);

  const handleSaveInfo = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...infoForm, industryId: infoForm.industryId ? parseInt(infoForm.industryId) : null };
      await updateCustomer(id, payload);
      setEditingInfo(false);
      fetchCustomer();
    } catch { setError('Failed to update'); }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...customer, industryId: customer.industryId ? parseInt(customer.industryId) : null, profileData: JSON.stringify(profileForm) };
      await updateCustomer(id, payload);
      setEditingProfile(false);
      fetchCustomer();
    } catch { setError('Failed to update profile'); }
  };

  const parsedProfileData = (() => {
    if (!customer?.profileData) return {};
    try { return JSON.parse(customer.profileData); } catch { return {}; }
  })();

  const templateSections = (() => {
    if (!profilingTemplate?.schema) return [];
    try {
      const schema = typeof profilingTemplate.schema === 'string' ? JSON.parse(profilingTemplate.schema) : profilingTemplate.schema;
      return schema.sections || [];
    } catch { return []; }
  })();

  const getProfileValue = (key) => {
    const val = profileForm[key];
    if (!val) return '';
    if (typeof val === 'object' && val.value !== undefined) return val.value || '';
    return String(val);
  };

  const setProfileValue = (key, value) => {
    setProfileForm({ ...profileForm, [key]: value });
  };

  const startEditProfile = () => {
    const flat = {};
    Object.entries(parsedProfileData).forEach(([sectionOrKey, val]) => {
      if (typeof val === 'object' && val !== null && !val.value && !Array.isArray(val)) {
        Object.entries(val).forEach(([fieldKey, fieldVal]) => {
          if (typeof fieldVal === 'object' && fieldVal !== null && fieldVal.value !== undefined) {
            flat[fieldKey] = fieldVal.value || '';
          } else {
            flat[fieldKey] = fieldVal || '';
          }
        });
      } else if (typeof val === 'object' && val !== null && val.value !== undefined) {
        flat[sectionOrKey] = val.value || '';
      } else {
        flat[sectionOrKey] = val || '';
      }
    });
    setProfileForm(flat);
    setEditingProfile(true);
  };

  const getDisplayValue = (key) => {
    if (parsedProfileData[key] !== undefined) {
      const val = parsedProfileData[key];
      if (typeof val === 'object' && val !== null && val.value !== undefined) return val.value;
      return String(val || '');
    }
    for (const [, sectionVal] of Object.entries(parsedProfileData)) {
      if (typeof sectionVal === 'object' && sectionVal !== null && !sectionVal.value) {
        if (sectionVal[key] !== undefined) {
          const val = sectionVal[key];
          if (typeof val === 'object' && val !== null && val.value !== undefined) return val.value;
          return String(val || '');
        }
      }
    }
    return '';
  };

  const handleDeleteContact = async (contactId) => {
    if (!(await dialog.confirm({ title: 'Delete contact', message: 'Delete this contact?', confirmText: 'Delete', danger: true }))) return;
    try {
      await deleteContact(id, contactId);
      fetchCustomer();
    } catch { setError('Failed to delete contact'); }
  };

  const openAddInteraction = () => {
    setInteractionForm({ type: 'Email', interactionDate: new Date().toISOString().slice(0, 16), subject: '', notes: '', conductedBy: '', contactPersonId: '', outcome: '', followUpAction: '', followUpDate: '' });
    setInteractionModal('add');
  };

  const openEditInteraction = (i) => {
    setInteractionForm({ ...i, interactionDate: new Date(i.interactionDate).toISOString().slice(0, 16), contactPersonId: i.contactPersonId || '', outcome: i.outcome || '', followUpAction: i.followUpAction || '', followUpDate: i.followUpDate ? new Date(i.followUpDate).toISOString().slice(0, 10) : '' });
    setInteractionModal(i);
  };

  const handleSaveInteraction = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...interactionForm, contactPersonId: interactionForm.contactPersonId || null, outcome: interactionForm.outcome || null, followUpDate: interactionForm.followUpDate || null };
      if (interactionModal === 'add') {
        await createInteraction(id, payload);
      } else {
        await updateInteraction(id, interactionModal.id, payload);
      }
      setInteractionModal(null);
      fetchCustomer();
    } catch { setError('Failed to save interaction'); }
  };

  const handleDeleteInteraction = async (intId) => {
    if (!(await dialog.confirm({ title: 'Delete interaction', message: 'Delete this interaction?', confirmText: 'Delete', danger: true }))) return;
    try {
      await deleteInteraction(id, intId);
      fetchCustomer();
    } catch { setError('Failed to delete interaction'); }
  };

  const formatDate = (d) => new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  const formatDateTime = (d) => new Date(d).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

  const TAB_LABELS = ['Company Info', 'Profile', customer ? `Contacts (${customer.contactPersons.length})` : 'Contacts', customer ? `Interactions (${customer.interactions.length})` : 'Interactions'];

  if (loading) return <Box sx={{ p: 5, textAlign: 'center' }}><CircularProgress /></Box>;
  if (!customer) return <Box sx={{ p: 5, textAlign: 'center' }}><Typography color="text.secondary">Customer not found</Typography></Box>;

  const contactColumns = [
    { key: 'name', header: 'Name', render: (_, row) => <Typography sx={{ fontWeight: 500 }}>{row.firstName} {row.lastName}</Typography> },
    { key: 'designation', header: 'Designation', render: (val) => val || '-' },
    { key: 'email', header: 'Email', render: (val) => val || '-' },
    { key: 'phone', header: 'Phone', render: (val) => val || '-' },
    {
      key: 'contactInfos', header: 'Additional Info', render: (val) => {
        if (val && val.length > 0) {
          return (
            <Stack spacing={0.25}>
              {val.map((ci) => (
                <Typography key={ci.id} variant="body2" sx={{ fontSize: '0.85em' }}>
                  <strong>{ci.fieldName}:</strong> {ci.value}
                </Typography>
              ))}
            </Stack>
          );
        }
        return '-';
      }
    },
    { key: 'isPrimary', header: 'Primary', render: (val) => val ? <Chip label="Primary" color="primary" size="small" /> : '-' },
    {
      key: 'actions', header: 'Actions', render: (_, row) => (
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="outlined" onClick={() => navigate(`/customers/${id}/contacts/${row.id}`)}>Edit</Button>
          <Button size="small" variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={() => handleDeleteContact(row.id)}>Delete</Button>
        </Stack>
      )
    },
  ];

  const interactionColumns = [
    { key: 'interactionDate', header: 'Date', render: (val) => formatDateTime(val) },
    { key: 'type', header: 'Type', render: (val) => <Chip label={val} color={TYPE_COLORS[val] || 'default'} size="small" /> },
    { key: 'subject', header: 'Subject', render: (val) => <Typography sx={{ fontWeight: 500 }}>{val}</Typography> },
    { key: 'contactPersonName', header: 'Contact Person', render: (val) => val || <Typography color="text.disabled">-</Typography> },
    {
      key: 'outcome', header: 'Outcome', render: (val) => val
        ? <Typography sx={{ color: OUTCOME_COLORS[val] || '#718096', fontWeight: 600 }}>{val}</Typography>
        : '-'
    },
    {
      key: 'followUpDate', header: 'Follow-Up', render: (val, row) => val
        ? <Typography sx={{ color: row.isFollowUpCompleted ? '#38a169' : '#e53e3e' }}>{formatDate(val)} {row.isFollowUpCompleted ? '\u2713' : ''}</Typography>
        : '-'
    },
    {
      key: 'actions', header: 'Actions', render: (_, row) => (
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="outlined" onClick={(e) => { e.stopPropagation(); openEditInteraction(row); }}>Edit</Button>
          <Button size="small" variant="outlined" color="primary" startIcon={<AutoFixHighIcon />} disabled={suggestingId === row.id}
            onClick={async (e) => {
              e.stopPropagation();
              setSuggestingId(row.id);
              try {
                const { data } = await aiSuggestNextSteps({ interactionId: row.id, customerId: parseInt(id) });
                setAiSuggestions({ interactionId: row.id, suggestions: data.suggestions });
              } catch { setError('AI suggestion failed'); }
              finally { setSuggestingId(null); }
            }}>{suggestingId === row.id ? '...' : 'AI Steps'}</Button>
          <Button size="small" variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={(e) => { e.stopPropagation(); handleDeleteInteraction(row.id); }}>Delete</Button>
        </Stack>
      )
    },
  ];

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/')} sx={{ mb: 2 }}>Back to Customers</Button>

      <Paper sx={{ p: 3, mb: 2.5 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} sx={{ justifyContent: 'space-between', alignItems: { md: 'center' }, gap: 2 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>{customer.companyName}</Typography>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mt: 0.5, flexWrap: 'wrap' }}>
              {customer.industryName && <Chip label={customer.industryName} size="small" color="primary" variant="outlined" />}
              {customer.country && (
                <Typography variant="body2" color="text.secondary">
                  {customer.countryCode && <Box component="span" sx={{ fontWeight: 600 }}>{customer.countryCode} </Box>}{customer.country}
                </Typography>
              )}
              {customer.email && <Typography variant="body2" color="text.secondary">{customer.email}</Typography>}
            </Stack>
          </Box>
          <Stack direction="row" spacing={0} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <Box sx={{ textAlign: 'center', px: 2 }}>
              <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: 'primary.main' }}>{customer.contactPersons.length}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 500 }}>Contacts</Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box sx={{ textAlign: 'center', px: 2 }}>
              <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: 'primary.main' }}>{customer.interactions.length}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 500 }}>Interactions</Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box sx={{ textAlign: 'center', px: 2 }}>
              <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: getScoreColor(customer.azotteFitScore) }}>{customer.azotteFitScore}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 500 }}>Fit Score</Typography>
            </Box>
          </Stack>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Tabs value={activeTab} onChange={(_, v) => {
        setActiveTab(v);
        const next = new URLSearchParams(searchParams);
        next.set('tab', CUSTOMER_TAB_KEYS[v]);
        setSearchParams(next, { replace: true });
      }} sx={{ mb: 2 }}>
        {TAB_LABELS.map((label, idx) => <Tab key={idx} label={label} />)}
      </Tabs>

      {activeTab === 0 && (
        <Paper sx={{ p: 3, mb: 2.5 }}>
          <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Typography variant="h6">Company Information</Typography>
            {!editingInfo && <Button variant="outlined" size="small" startIcon={<EditIcon />} onClick={() => setEditingInfo(true)}>Edit</Button>}
          </Stack>
          {editingInfo ? (
            <form onSubmit={handleSaveInfo}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField size="small" fullWidth required label="Company Name" value={infoForm.companyName || ''} onChange={(e) => setInfoForm({ ...infoForm, companyName: e.target.value })} />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField select size="small" fullWidth label="Industry" value={infoForm.industryId || ''} onChange={(e) => setInfoForm({ ...infoForm, industryId: e.target.value })}>
                    <MenuItem value="">-- Select Industry --</MenuItem>
                    {industries.map((ind) => <MenuItem key={ind.id} value={ind.id}>{ind.name}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField size="small" fullWidth label="Website" value={infoForm.website || ''} onChange={(e) => setInfoForm({ ...infoForm, website: e.target.value })} />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField size="small" fullWidth type="email" label="Email" value={infoForm.email || ''} onChange={(e) => setInfoForm({ ...infoForm, email: e.target.value })} />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField size="small" fullWidth label="Phone" value={infoForm.phone || ''} onChange={(e) => setInfoForm({ ...infoForm, phone: e.target.value })} />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField size="small" fullWidth label="Address" value={infoForm.address || ''} onChange={(e) => setInfoForm({ ...infoForm, address: e.target.value })} />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField select size="small" fullWidth label="Country" value={infoForm.country || ''} onChange={(e) => {
                    const selected = countries.find(c => c.name === e.target.value);
                    setInfoForm({ ...infoForm, country: e.target.value, countryCode: selected ? selected.code : '' });
                  }}>
                    <MenuItem value="">-- Select Country --</MenuItem>
                    {countries.map((c) => <MenuItem key={c.code} value={c.name}>{c.name} ({c.code})</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField size="small" fullWidth label="Country Code" value={infoForm.countryCode || ''} disabled sx={{ '& .MuiInputBase-root': { bgcolor: 'action.hover' } }} />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }} />
                <Grid size={{ xs: 12 }}>
                  <TextField size="small" fullWidth multiline rows={3} label="Notes" value={infoForm.notes || ''} onChange={(e) => setInfoForm({ ...infoForm, notes: e.target.value })} />
                </Grid>
              </Grid>
              <DialogActions sx={{ px: 0, pt: 2 }}>
                <Button variant="outlined" onClick={() => { setEditingInfo(false); setInfoForm(customer); }}>Cancel</Button>
                <Button variant="contained" type="submit">Save Changes</Button>
              </DialogActions>
            </form>
          ) : (
            <Grid container spacing={2}>
              {[
                ['Industry', customer.industryName || '-'],
                ['Country', customer.country ? <>{customer.countryCode && <Box component="span" sx={{ fontWeight: 600 }}>{customer.countryCode} </Box>}{customer.country}</> : '-'],
                ['Email', customer.email || '-'],
                ['Phone', customer.phone || '-'],
                ['Address', customer.address || '-'],
                ['Website', customer.website ? <a href={customer.website} target="_blank" rel="noreferrer">{customer.website}</a> : '-'],
                ['Notes', customer.notes || '-'],
                ['Created', formatDate(customer.createdAt)],
                ['Last Updated', formatDate(customer.updatedAt)],
              ].map(([label, value]) => (
                <Grid key={label} size={{ xs: 12, sm: 6, md: 4 }}>
                  <Typography variant="caption" color="text.secondary">{label}</Typography>
                  <Typography variant="body2">{value}</Typography>
                </Grid>
              ))}
            </Grid>
          )}
        </Paper>
      )}

      {activeTab === 1 && (
        <Paper sx={{ p: 3, mb: 2.5 }}>
          <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Typography variant="h6">Company Profile</Typography>
            {!editingProfile && <Button variant="outlined" size="small" startIcon={<EditIcon />} onClick={startEditProfile}>Edit</Button>}
          </Stack>

          <Stack direction="row" sx={{ alignItems: 'center', gap: 2, py: 2, mb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ position: 'relative', width: 80, height: 80 }}>
              <svg viewBox="0 0 36 36" style={{ width: 80, height: 80, transform: 'rotate(-90deg)' }}>
                <circle cx="18" cy="18" r="15.9155" fill="none" stroke="#e0e0e0" strokeWidth="3" />
                <circle cx="18" cy="18" r="15.9155" fill="none" stroke={getScoreColor(customer.azotteFitScore)} strokeWidth="3"
                  strokeDasharray={`${customer.azotteFitScore} ${100 - customer.azotteFitScore}`} strokeLinecap="round" />
              </svg>
              <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.25rem', fontWeight: 700, color: getScoreColor(customer.azotteFitScore) }}>
                {customer.azotteFitScore}
              </Box>
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 600 }}>Azotte Fit Score</Typography>
              <Typography variant="body2" sx={{ color: getScoreColor(customer.azotteFitScore), fontWeight: 500 }}>{getScoreLabel(customer.azotteFitScore)}</Typography>
              <Typography variant="caption" color="text.secondary">Auto-calculated based on profile data</Typography>
            </Box>
          </Stack>

          {editingProfile ? (
            <form onSubmit={handleSaveProfile}>
              {templateSections.length > 0 ? templateSections.map(section => (
                <Box key={section.name} sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.secondary', borderBottom: '1px solid', borderColor: 'divider', pb: 1, mb: 1.5 }}>{section.name}</Typography>
                  <Grid container spacing={2}>
                    {(section.fields || []).map(f => (
                      <Grid key={f.key} size={{ xs: 12, sm: 6, md: 4 }}>
                        {f.type === 'text' || f.type === 'number' ? (
                          <TextField size="small" fullWidth type={f.type} label={f.label} value={getProfileValue(f.key)} onChange={(e) => setProfileValue(f.key, e.target.value)} />
                        ) : f.type === 'boolean' ? (
                          <TextField select size="small" fullWidth label={f.label} value={getProfileValue(f.key)} onChange={(e) => setProfileValue(f.key, e.target.value)}>
                            <MenuItem value="">-- Select --</MenuItem>
                            <MenuItem value="Yes">Yes</MenuItem>
                            <MenuItem value="No">No</MenuItem>
                          </TextField>
                        ) : f.type === 'select' && f.options ? (
                          <TextField select size="small" fullWidth label={f.label} value={getProfileValue(f.key)} onChange={(e) => setProfileValue(f.key, e.target.value)}>
                            <MenuItem value="">-- Select --</MenuItem>
                            {f.options.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                          </TextField>
                        ) : (
                          <TextField size="small" fullWidth label={f.label} value={getProfileValue(f.key)} onChange={(e) => setProfileValue(f.key, e.target.value)} />
                        )}
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )) : (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography color="text.secondary">No profiling template configured. Create one in Management &rarr; Profiling Templates.</Typography>
                </Box>
              )}
              <DialogActions sx={{ px: 0, pt: 2 }}>
                <Button variant="outlined" onClick={() => { setEditingProfile(false); }}>Cancel</Button>
                <Button variant="contained" type="submit">Save Profile</Button>
              </DialogActions>
            </form>
          ) : (
            <Box>
              {templateSections.length > 0 ? templateSections.map(section => (
                <Box key={section.name} sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.secondary', borderBottom: '1px solid', borderColor: 'divider', pb: 1, mb: 1.5 }}>{section.name}</Typography>
                  <Grid container spacing={2}>
                    {(section.fields || []).map(f => {
                      const val = getDisplayValue(f.key);
                      return (
                        <Grid key={f.key} size={{ xs: 12, sm: 6, md: 4 }}>
                          <Typography variant="caption" color="text.secondary">{f.label}</Typography>
                          <Typography variant="body2">{val || '-'}</Typography>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              )) : Object.keys(parsedProfileData).length > 0 ? (
                Object.entries(parsedProfileData).map(([section, fields]) => (
                  <Box key={section} sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.secondary', borderBottom: '1px solid', borderColor: 'divider', pb: 1, mb: 1.5 }}>{section}</Typography>
                    <Grid container spacing={2}>
                      {typeof fields === 'object' && fields !== null && Object.entries(fields).map(([key, val]) => (
                        <Grid key={key} size={{ xs: 12, sm: 6, md: 4 }}>
                          <Typography variant="caption" color="text.secondary">{key}</Typography>
                          <Typography variant="body2">{typeof val === 'object' && val !== null && val.value !== undefined ? val.value || '-' : String(val || '-')}</Typography>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                ))
              ) : (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography color="text.secondary">No profile data yet. Click Edit to add profiling data, or use AI Enrichment from the Profile page.</Typography>
                </Box>
              )}
            </Box>
          )}
        </Paper>
      )}

      {activeTab === 2 && (
        <Paper sx={{ p: 3, mb: 2.5 }}>
          {customer.contactPersons.length === 0 ? (
            <>
              <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="h6">Contact Persons</Typography>
              </Stack>
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary" sx={{ mb: 1 }}>No contacts yet</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate(`/customers/${id}/contacts/new`)}>Add First Contact</Button>
              </Box>
            </>
          ) : (
            <AzotteGrid
              columns={contactColumns}
              rows={customer.contactPersons.slice(contactsPage * contactsRowsPerPage, contactsPage * contactsRowsPerPage + contactsRowsPerPage)}
              totalCount={customer.contactPersons.length}
              page={contactsPage}
              setPage={setContactsPage}
              rowsPerPage={contactsRowsPerPage}
              handleRowsPerPageChange={(e) => { setContactsRowsPerPage(parseInt(e.target.value, 10)); setContactsPage(0); }}
              loading={false}
              onAdd={() => navigate(`/customers/${id}/contacts/new`)}
              title="Contact Persons"
              actionColumnKey="actions"
            />
          )}
        </Paper>
      )}

      {activeTab === 3 && (
        <Paper sx={{ p: 3, mb: 2.5 }}>
          {customer.interactions.length === 0 ? (
            <>
              <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="h6">Interaction History</Typography>
              </Stack>
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary" sx={{ mb: 1 }}>No interactions yet</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={openAddInteraction}>Add First Interaction</Button>
              </Box>
            </>
          ) : (
            <AzotteGrid
              columns={interactionColumns}
              rows={customer.interactions}
              totalCount={customer.interactions.length}
              page={interactionsPage}
              setPage={setInteractionsPage}
              rowsPerPage={interactionsRowsPerPage}
              handleRowsPerPageChange={(e) => { setInteractionsRowsPerPage(parseInt(e.target.value)); setInteractionsPage(0); }}
              loading={false}
              onDoubleClick={(row) => setSelectedInteraction(row)}
              onAdd={openAddInteraction}
              title="Interaction History"
              actionColumnKey="actions"
            />
          )}

          {aiSuggestions && (
            <Box sx={{ pt: 2, borderTop: '1px solid', borderColor: 'divider', mt: 2 }}>
              <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>AI Suggested Next Steps</Typography>
                <Button size="small" variant="outlined" startIcon={<CloseIcon />} onClick={() => setAiSuggestions(null)}>Close</Button>
              </Stack>
              {aiSuggestions.suggestions?.map((s, idx) => (
                <Paper key={idx} variant="outlined" sx={{ p: 1.5, mb: 1, bgcolor: 'action.hover' }}>
                  <Typography sx={{ fontWeight: 600 }}>{s.action}</Typography>
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mt: 0.5 }}>
                    <Chip label={s.type} size="small" />
                    {s.recommendedDate && <Typography variant="body2">Recommended: {s.recommendedDate}</Typography>}
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{s.reasoning}</Typography>
                  {s.talkingPoints?.length > 0 && (
                    <Box component="ul" sx={{ m: '4px 0 0', pl: 2.5, fontSize: '0.85rem' }}>
                      {s.talkingPoints.map((tp, i) => <li key={i}>{tp}</li>)}
                    </Box>
                  )}
                  <Button variant="contained" size="small" sx={{ mt: 1 }}
                    onClick={() => {
                      setInteractionForm({
                        type: s.type || 'Email',
                        interactionDate: new Date().toISOString().slice(0, 16),
                        subject: s.action,
                        notes: s.talkingPoints?.join('\n') || '',
                        conductedBy: '', contactPersonId: '', outcome: '',
                        followUpAction: s.action,
                        followUpDate: s.recommendedDate?.includes('-') ? s.recommendedDate : '',
                      });
                      setInteractionModal('add');
                      setAiSuggestions(null);
                    }}>Use This</Button>
                </Paper>
              ))}
            </Box>
          )}

          {selectedInteraction && (
            <Box sx={{ borderTop: '1px solid', borderColor: 'divider', mt: 2, pt: 2 }}>
              <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Notes</Typography>
                  <Chip label={selectedInteraction.subject} size="small" color="primary" variant="outlined" />
                  <Chip label={selectedInteraction.type} size="small" color={TYPE_COLORS[selectedInteraction.type] || 'default'} />
                  <Typography variant="body2" color="text.secondary">{formatDateTime(selectedInteraction.interactionDate)}</Typography>
                </Stack>
                <Button size="small" variant="text" onClick={() => setSelectedInteraction(null)}>{'\u2715'} Close</Button>
              </Stack>
              <Paper variant="outlined" sx={{ p: '14px 18px', fontSize: '0.875rem', lineHeight: 1.7, color: 'text.secondary', whiteSpace: 'pre-wrap', minHeight: 60, maxHeight: 300, overflowY: 'auto', bgcolor: 'action.hover' }}>
                {selectedInteraction.notes || <Typography component="span" color="text.disabled" sx={{ fontStyle: 'italic' }}>No notes for this interaction.</Typography>}
              </Paper>
            </Box>
          )}
        </Paper>
      )}

      {interactionModal && (
        <Modal title={interactionModal === 'add' ? 'Add Interaction' : 'Edit Interaction'} onClose={() => setInteractionModal(null)}>
          <form onSubmit={handleSaveInteraction}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField select size="small" fullWidth required label="Type" value={interactionForm.type || 'Email'} onChange={(e) => setInteractionForm({ ...interactionForm, type: e.target.value })}>
                  {INTERACTION_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField size="small" fullWidth required type="datetime-local" label="Date" value={interactionForm.interactionDate || ''} onChange={(e) => setInteractionForm({ ...interactionForm, interactionDate: e.target.value })} slotProps={{ inputLabel: { shrink: true } }} />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField size="small" fullWidth required label="Subject" value={interactionForm.subject || ''} onChange={(e) => setInteractionForm({ ...interactionForm, subject: e.target.value })} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField select size="small" fullWidth label="Contact Person" value={interactionForm.contactPersonId || ''} onChange={(e) => setInteractionForm({ ...interactionForm, contactPersonId: e.target.value ? parseInt(e.target.value) : '' })}>
                  <MenuItem value="">-- None --</MenuItem>
                  {customer.contactPersons.map((cp) => <MenuItem key={cp.id} value={cp.id}>{cp.firstName} {cp.lastName}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField size="small" fullWidth label="Conducted By" value={interactionForm.conductedBy || ''} onChange={(e) => setInteractionForm({ ...interactionForm, conductedBy: e.target.value })} />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField size="small" fullWidth multiline rows={4} label="Notes" value={interactionForm.notes || ''} onChange={(e) => setInteractionForm({ ...interactionForm, notes: e.target.value })} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField select size="small" fullWidth label="Outcome" value={interactionForm.outcome || ''} onChange={(e) => setInteractionForm({ ...interactionForm, outcome: e.target.value })}>
                  <MenuItem value="">-- Select --</MenuItem>
                  {['Positive', 'Neutral', 'Negative', 'NoResponse', 'FollowUpNeeded'].map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField size="small" fullWidth type="date" label="Follow-Up Date" value={interactionForm.followUpDate || ''} onChange={(e) => setInteractionForm({ ...interactionForm, followUpDate: e.target.value })} slotProps={{ inputLabel: { shrink: true } }} />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField size="small" fullWidth label="Follow-Up Action" value={interactionForm.followUpAction || ''} onChange={(e) => setInteractionForm({ ...interactionForm, followUpAction: e.target.value })} />
              </Grid>
            </Grid>
            <DialogActions sx={{ px: 0, pt: 2 }}>
              <Button variant="outlined" onClick={() => setInteractionModal(null)}>Cancel</Button>
              <Button variant="contained" type="submit">{interactionModal === 'add' ? 'Add Interaction' : 'Save Changes'}</Button>
            </DialogActions>
          </form>
        </Modal>
      )}
    </Box>
  );
}
